import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { GIG_CATEGORIES, formatCategoryName } from '../constants/categories';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  Eye,
  Edit,
  Trash2,
  Plus,
  Archive
} from 'lucide-react';

const MyGigsPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    status: '',
    search: '',
    page: 1
  });
  const [archiveModal, setArchiveModal] = useState({ isOpen: false, gigId: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, gigId: null });

  // Archive gig mutation - must be called before any early returns
  const archiveGigMutation = useMutation(
    (gigId) => api.put(API_ENDPOINTS.GIGS.ARCHIVE(gigId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['myGigs']);
        toast.success('Gig archived successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to archive gig');
      }
    }
  );

  // Unarchive gig mutation - must be called before any early returns
  const unarchiveGigMutation = useMutation(
    (gigId) => api.put(API_ENDPOINTS.GIGS.UNARCHIVE(gigId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['myGigs']);
        toast.success('Gig unarchived successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to unarchive gig');
      }
    }
  );

  // Delete gig mutation
  const deleteGigMutation = useMutation(
    (gigId) => api.delete(API_ENDPOINTS.GIGS.DELETE(gigId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['myGigs']);
        toast.success('Gig deleted successfully');
        setDeleteModal({ isOpen: false, gigId: null });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete gig');
      }
    }
  );

  // Fetch user's gigs
  const { data: gigsData, isLoading, error } = useQuery(
    ['myGigs', filters],
    () => api.get(API_ENDPOINTS.GIGS.MY_GIGS, { params: filters }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
      enabled: user?.role === 'talent', // Only fetch if user is a talent
    }
  );

  // Check if user is a talent
  if (user?.role !== 'talent') {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🚫</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Only talents can view their gigs. Please switch to talent role.
          </p>
        </div>
      </div>
    );
  }

  const gigs = gigsData?.gigs || [];
  const totalPages = gigsData?.totalPages || 1;
  const currentPage = gigsData?.currentPage || 1;

  const categories = Object.keys(GIG_CATEGORIES);

  const gigTypes = [
    { value: '', label: 'All Types' },
    { value: 'professional', label: 'Professional Services' },
    { value: 'labour', label: 'Physical Labor' }
  ];

  const statuses = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleArchiveGig = (gigId) => {
    setArchiveModal({ isOpen: true, gigId });
  };

  const handleConfirmArchive = () => {
    if (archiveModal.gigId) {
      archiveGigMutation.mutate(archiveModal.gigId);
      setArchiveModal({ isOpen: false, gigId: null });
    }
  };

  const handleUnarchiveGig = (gigId) => {
    unarchiveGigMutation.mutate(gigId);
  };

  const handleDeleteGig = (gigId) => {
    setDeleteModal({ isOpen: true, gigId });
  };

  const handleConfirmDelete = () => {
    if (deleteModal.gigId) {
      deleteGigMutation.mutate(deleteModal.gigId);
    }
  };

  const formatPrice = (gig) => {
    if (!gig.pricing) return 'Price negotiable';
    
    // New structure: basic/premium are objects with price property
    if (gig.pricing.basic) {
      const basicPrice = typeof gig.pricing.basic === 'object' && gig.pricing.basic.price 
        ? gig.pricing.basic.price 
        : gig.pricing.basic;
      const premiumPrice = gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price
        ? gig.pricing.premium.price
        : (typeof gig.pricing.premium === 'number' ? gig.pricing.premium : null);
      
      if (premiumPrice) {
        return `$${Number(basicPrice).toFixed(2)} - $${Number(premiumPrice).toFixed(2)}`;
      }
      return `$${Number(basicPrice).toFixed(2)}`;
    }
    
    // Legacy structure: basic/standard/premium are numbers
    if (typeof gig.pricing.basic === 'number') return `$${gig.pricing.basic.toFixed(2)}`;
    if (typeof gig.pricing.standard === 'number') return `$${gig.pricing.standard.toFixed(2)}`;
    if (typeof gig.pricing.premium === 'number') return `$${gig.pricing.premium.toFixed(2)}`;
    
    // Min/max range pricing
    if (gig.pricing.min && gig.pricing.max) {
      return `$${gig.pricing.min.toFixed(2)} - $${gig.pricing.max.toFixed(2)}`;
    }
    if (gig.pricing.min) return `$${gig.pricing.min.toFixed(2)}+`;
    
    return 'Price negotiable';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error loading gigs
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error.message || 'Something went wrong while loading your gigs.'}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading mb-2">
              My Gigs
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage your service offerings and track performance
            </p>
          </div>
          <Link to="/create-gig" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create New Gig
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search your gigs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full input-field"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {GIG_CATEGORIES[cat]?.label || formatCategoryName(cat)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full input-field"
            >
              {gigTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full input-field"
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button className="btn-outline w-full">
              <Filter className="w-4 h-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Gigs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {gigs.map((gig) => (
          <div key={gig._id} className="card p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3 flex-wrap gap-2">
                  <Link 
                    to={`/gigs/${gig._id}`}
                    className="text-xl font-semibold text-heading hover:text-accent transition-colors duration-200"
                  >
                    {gig.title}
                  </Link>
                  {/* Order Status Counts - Show all statuses with counts */}
                  {gig.orderStatusCounts && (
                    <>
                      {gig.orderStatusCounts.completed > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Completed ({gig.orderStatusCounts.completed})
                        </span>
                      )}
                      {gig.orderStatusCounts.active > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Active ({gig.orderStatusCounts.active})
                        </span>
                      )}
                      {gig.orderStatusCounts.pending > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          Pending ({gig.orderStatusCounts.pending})
                        </span>
                      )}
                      {gig.orderStatusCounts.archived > 0 && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                          Archived ({gig.orderStatusCounts.archived})
                        </span>
                      )}
                    </>
                  )}
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                  {stripHtmlTags(gig.description)}
                </p>

                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4" />
                    <span>{formatPrice(gig)}</span>
                  </div>
                  
                  {gig.deliveryTime && gig.deliveryTime.value && gig.deliveryTime.unit && (
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{gig.deliveryTime.value} {gig.deliveryTime.unit}</span>
                    </div>
                  )}

                  {gig.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{gig.location.remote ? 'Remote' : gig.location.city || 'Location not specified'}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <div className="flex items-center space-x-1">
                    <Eye className="w-4 h-4" />
                    <span>{gig.views || 0} views</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{gig.orderCount || 0} orders</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4" />
                    <span>{gig.rating?.average?.toFixed(1) || '0'} ({gig.rating?.count || 0})</span>
                  </div>
                </div>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {gig.skills?.slice(0, 4).map((skill, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      {skill}
                    </span>
                  ))}
                  {gig.skills?.length > 4 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 text-xs rounded-md">
                      +{gig.skills.length - 4} more
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      Created {new Date(gig.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/gigs/${gig._id}/orders`}
                      className="btn-outline text-sm"
                      title="View applications / orders"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Applications
                    </Link>
                    {gig.status !== 'archived' ? (
                      <>
                        {/* <Link
                          to={`/gigs/${gig._id}/edit`}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
                        >
                          <Edit className="w-4 h-4" />
                        </Link> */}
                        <button
                          onClick={() => handleArchiveGig(gig._id)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-orange-600 transition-colors duration-200"
                          title="Archive gig"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleUnarchiveGig(gig._id)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 transition-colors duration-200"
                        title="Unarchive gig"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                    <button 
                      onClick={() => handleDeleteGig(gig._id)}
                      className="p-2 text-red-600 hover:text-red-700 transition-colors duration-200"
                      title="Delete gig"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = Math.max(1, currentPage - 2) + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-2 text-sm rounded-lg ${
                  page === currentPage
                    ? 'bg-accent text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && gigs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No gigs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You haven't created any gigs yet. Start by creating your first service offering.
          </p>
          <Link to="/create-gig" className="btn-primary">
            Create Your First Gig
          </Link>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveModal.isOpen}
        onClose={() => setArchiveModal({ isOpen: false, gigId: null })}
        onConfirm={handleConfirmArchive}
        title="Archive Gig"
        message="Are you sure you want to archive this gig? It will be hidden from public listings."
        confirmText="Archive"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, gigId: null })}
        onConfirm={handleConfirmDelete}
        title="Delete Gig"
        message="Are you sure you want to delete this gig? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default MyGigsPage;
